import { Redirect, router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';
import { FullScreenSpinner, useToastController } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import {
    mapPartialBillToBill,
    transformReviewBillFromServer,
} from '../../common/utils/billUtils';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useUpdateBill from '../hooks/useUpdateBill';
import { billsToReviewAtom, selectedReviewBillAtom } from '../state/BillsAtoms';
import type { IBill, PartialBill } from '../types/Bill';
import BillDetailsEditor from './BillDetailsEditor/BillDetailsEditor';

/*
 * Bill editor from the "Does this look right" page
 */
const EditReviewBill = () => {
    LogRocketFacade.tagPage('EditReviewBill');
    const toast = useToastController();

    const user = useAuthedUser();
    const { mutate: updateBill } = useUpdateBill();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const bill = useAtomValue(selectedReviewBillAtom);
    const storeBillsToReview = useSetAtom(billsToReviewAtom);

    if (!bill) {
        captureException(
            new Error('Attempted to load EditReviewBill with no selected Bill'),
        );
        return <Redirect href="/bills/bills-discovery-review" />;
    }

    const backToBillDiscovery = () => {
        router.navigate('/bills/bills-discovery-review');
    };

    const onSubmit = (updatedBill: IBill) => {
        setIsSubmitting(true);

        const {
            id,
            accountId,
            displayName,
            payee,
            amount,
            cadence,
            timingLabel,
            timingDescription,
            category,
            lastPaidAt,
            memberMarkedAsSwitched,
        } = updatedBill;
        updateBill(
            {
                urlParams: {
                    id,
                    membershipId: user?.membership?.id ?? '',
                },
                body: {
                    accountId,
                    displayName,
                    payee,
                    amount,
                    cadence,
                    timingLabel,
                    timingDescription,
                    category,
                    lastPaidAt,
                    memberMarkedAsSwitched,
                },
            },
            {
                onSuccess: (updatedBillFromServer) => {
                    const transformedBill = transformReviewBillFromServer(
                        mapPartialBillToBill(updatedBillFromServer),
                    );

                    storeBillsToReview((prev) =>
                        prev.map((pb) =>
                            pb.id === transformedBill.id ? transformedBill : pb,
                        ),
                    );
                    toast.show(`${payee} bill updated`);
                    setIsSubmitting(false);
                    backToBillDiscovery();
                },
                onError: (error) => {
                    toast.show('Something went wrong');
                    captureException(error);
                    setIsSubmitting(false);
                    // TODO handle error UX
                },
            },
        );
    };

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    return (
        <BillDetailsEditor
            bill={bill satisfies PartialBill}
            title="Edit"
            submitButtonLabel="Update"
            onSubmit={onSubmit}
            onBack={backToBillDiscovery}
        />
    );
};

export default EditReviewBill;
